import { Component, ElementRef, inject, signal, ChangeDetectionStrategy, viewChild } from '@angular/core';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatBadgeModule } from '@angular/material/badge';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TextFieldModule } from '@angular/cdk/text-field';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { BillingPeriod, CustomerAccessStatus, Note, NoteService } from '@eadsysteme/billingclient-v1';
import { SessionService } from '../../../../services/session.service';
import { DateUtil } from '../../../../util/DateUtil';
import { SnackbarService } from '../../../../services/snackbar.service';
import { finalize } from 'rxjs/operators';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ConfirmationDialogComponent } from '../../../confirmation-dialog/confirmation-dialog.component';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { Observable } from 'rxjs';

@Component({
	selector: 'vex-notes-dialog',
	imports: [
		FormsModule,
		ReactiveFormsModule,
		MatDialogModule,
		MatButtonModule,
		MatIconModule,
		MatInputModule,
		MatFormFieldModule,
		MatBadgeModule,
		MatTooltipModule,
		TextFieldModule,
		TranslocoDirective,
		MatProgressSpinnerModule,
		CdkDrag,
		CdkDragHandle,
	],
	templateUrl: './notes-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './notes-dialog.component.scss',
})
export class NotesDialogComponent {
	private readonly dialogRef = inject(MatDialogRef<NotesDialogComponent>);
	private readonly data = inject(MAT_DIALOG_DATA);
	private readonly noteService = inject(NoteService);
	private readonly sessionService = inject(SessionService);
	private readonly translocoService = inject(TranslocoService);
	private readonly snackBar = inject(SnackbarService);
	private readonly dialog = inject(MatDialog);
	protected readonly dateUtil = inject(DateUtil);

	readonly notesList = viewChild.required<ElementRef<HTMLElement>>('notesList');

	protected readonly NOTES_TEXT_MAXLENGTH = 255;

	period = signal<BillingPeriod>(this.data.period);
	billingId = this.data.billingId;
	notes = signal<Note[]>([]);

	editingNoteExportId = signal<string | null>(null);
	private previousNoteText: string | null = null;
	private notesChanged = false;
	loading = signal(false);

	constructor() {
		this.loadNotes();
	}

	private isTempNote(note: Note): boolean {
		return !!note.exportId?.startsWith('temp-');
	}

	get subtitle(): string {
		const from = this.dateUtil.setDateFormatByActiveLang(this.period().validFrom, '');
		const to = this.dateUtil.setDateFormatByActiveLang(this.period().validTo, '');
		return `${from} - ${to}`;
	}

	protected addNote() {
		const tempExportId = 'temp-' + Date.now();
		const newNote: Note = { exportId: tempExportId, text: '' };
		this.notes.update((notes) => [...notes, newNote]);
		this.previousNoteText = '';
		this.editingNoteExportId.set(tempExportId);

		setTimeout(() => {
			const notesList = this.notesList();
			if (notesList) {
				notesList.nativeElement.scrollTop = notesList.nativeElement.scrollHeight;
			}
		}, 0);
	}

	protected unlockNote(note: Note) {
		if (this.period().locked) {
			return;
		}
		this.previousNoteText = note.text;
		this.editingNoteExportId.set(note.exportId);
	}

	protected lockNote(note: Note) {
		if (this.isTempNote(note)) {
			this.notes.update((notes) => notes.filter((n) => n.exportId !== note.exportId));
		} else if (this.previousNoteText !== null) {
			note.text = this.previousNoteText;
		}
		this.editingNoteExportId.set(null);
		this.previousNoteText = null;
	}

	protected saveNote(note: Note) {
		this.submitNoteRequest(note, this.isTempNote(note) ? 'create' : 'update');
	}

	protected deleteNote(note: Note) {
		if (this.isTempNote(note)) {
			this.notes.update((notes) => notes.filter((n) => n.exportId !== note.exportId));
			this.editingNoteExportId.set(null);
			return;
		}

		const dialogRef = this.dialog.open(ConfirmationDialogComponent);
		dialogRef.componentInstance.titleMessage = this.translocoService.translate('common.attention');
		dialogRef.componentInstance.confirmMessage = this.translocoService.translate('common.confirmDelete');

		dialogRef.afterClosed().subscribe((confirm) => {
			if (confirm) {
				this.submitNoteRequest(note, 'delete');
			}
		});
	}

	private submitNoteRequest(note: Note, method: 'create' | 'update' | 'delete') {
		if (!note || !['create', 'update', 'delete'].includes(method)) {
			return;
		}

		this.loading.set(true);
		if (method === 'delete') {
			this.noteService
				.deleteNote(this.sessionService.effectiveArea, this.billingId, note.exportId)
				.pipe(finalize(() => this.loading.set(false)))
				.subscribe({
					next: () => {
						this.notesChanged = true;
						this.loadNotes();
						this.snackBar.success(this.translocoService.translate('common.deleted'));
					},
					error: () => this.snackBar.error(this.translocoService.translate('errors.checkInput')),
				});
		} else {
			let request: Observable<Note>;
			if (method === 'create') {
				const noteToCreate: Note = { ...note, exportId: undefined };
				request = this.noteService.createNote(this.sessionService.effectiveArea, this.billingId, this.period().exportId, noteToCreate);
			} else if (method === 'update') {
				request = this.noteService.updateNote(this.sessionService.effectiveArea, this.billingId, note.exportId, note);
			}
			request.pipe(finalize(() => this.loading.set(false))).subscribe({
				next: () => {
					this.notesChanged = true;
					this.loadNotes();
					this.editingNoteExportId.set(null);
					this.snackBar.success(this.translocoService.translate('common.saved'));
				},
				error: () => this.snackBar.error(this.translocoService.translate('errors.checkInput')),
			});
		}
	}

	private loadNotes() {
		this.loading.set(true);
		this.noteService
			.readAllNote(this.sessionService.effectiveArea, this.billingId, this.period().exportId)
			.pipe(finalize(() => this.loading.set(false)))
			.subscribe({
				next: (notes) => this.notes.set(notes ?? []),
				error: () => this.snackBar.error(this.translocoService.translate('errors.loading')),
			});
	}

	close() {
		this.dialogRef.close(this.notesChanged ? this.period() : null);
	}

	protected readonly CustomerAccessStatus = CustomerAccessStatus;
}
